import { isWoodyError } from '../../errors/errors.utils.js';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { resourceQueryKeys } from './resourceQueryKeys.js';
import { useWoody } from '../../woody/woody.js';
import { isQueryEnabled } from '@fabric-app/queries/utilities';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { getCompatEnv } from '@fabric-app/compat/capabilities/environment';
import { getUserListViewFilters } from '../utils/getUserListViewPreferences.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';

const emptyResources = [];
const emptyTotal = 0;
const filterSelector = (data) => {
  const allResources = data.pages.flatMap((page) => page.resources);
  return {
    total: data.pages[0].total,
    pages: data.pages.map((page) => ({
      nextCursor: page.nextCursor,
      pageTotal: page.resources.length
    })),
    resources: allResources
  };
};
const useQueryResourceList = (options, queryOptions) => {
  const { client } = useWoody();
  const { data: user } = useQueryUserInfo();
  const contextAccessToken = useContextAccessToken();
  const userFilters = getUserListViewFilters(options, user?.id);
  const mergedOptions = {
    /**
    * default order
    */
    order: {
      property: "modifiedAt",
      direction: "DESC"
    },
    limit: getCompatEnv().isFabricProd ? 50 : 20,
    ...userFilters,
    kind: userFilters?.kind && userFilters.kind.length > 0 ? userFilters.kind : void 0,
    tagIds: userFilters?.tagIds && userFilters.tagIds.length > 0 ? userFilters.tagIds : void 0,
    userIds: userFilters?.userIds && userFilters.userIds.length > 0 ? userFilters.userIds : void 0
  };
  const query = useInfiniteQuery({
    queryKey: resourceQueryKeys.resourceList(mergedOptions),
    queryFn: async ({ pageParam }) => {
      const { accessToken, ...options2 } = {
        ...mergedOptions,
        cursor: pageParam
      };
      const pageData = await client.v2("/v2/resources/filter", {
        method: "post",
        body: options2,
        query: {
          accessToken: accessToken || contextAccessToken
        }
      });
      return {
        ...pageData,
        // This allows the select function to have access to the options used in the query
        // And we avoid having to memoize the select function
        _meta: {
          options: options2
        }
      };
    },
    placeholderData: keepPreviousData,
    refetchInterval: 45e3,
    retry: (failureCount, error) => {
      if (isWoodyError(error) && error.status === 404) {
        return failureCount < 1;
      }
      return failureCount < 4;
    },
    ...queryOptions,
    select: filterSelector,
    enabled: isQueryEnabled([
      queryOptions?.enabled
    ]),
    initialPageParam: void 0,
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore && lastPage?.nextCursor || void 0;
    }
  });
  return {
    ...query,
    resources: query.data?.resources || emptyResources,
    total: query.data?.total ?? emptyTotal
  };
};

export { emptyResources, emptyTotal, useQueryResourceList };
//# sourceMappingURL=useQueryResourceList.js.map
